import { portalRpc, asArray } from './portalRpc';
import type {
  PartnerActivityLog,
  PartnerRequestsFilter,
  PartnerServiceFilter,
  PartnerServiceRequest,
  PartnerServiceRequestFormData,
  Service,
  ServiceCategory
} from '../../types';

/**
 * Portale partner: catalogo servizi, richieste di preventivo, registro attività.
 *
 * Il portale partner gira sulle stesse tabelle del portale collaboratore, quindi le RPC usano
 * lo stesso resolver di sessione. Il filtro per organizzazione del partner avviene lato server:
 * prima il catalogo era esposto con il solo predicato `partner_visible = true`, cioè i listini
 * di tutte le organizzazioni.
 */

const PORTAL = 'partner' as const;

/**
 * Eventi ammessi da `portal_partner_log_activity`. Fuori da questa lista la RPC solleva
 * PORTAL_INVALID_ACTIVITY_TYPE: si evita di chiamarla per non generare rumore.
 */
const ALLOWED_ACTIVITY_TYPES = new Set([
  'view_services',
  'view_service_detail',
  'view_requests',
  'view_dashboard',
  'download_document',
  'request_quote'
]);

interface PartnerServicesResult {
  services: Service[] | null;
  categories: ServiceCategory[] | null;
}

/**
 * Catalogo dei servizi visibili al partner e categorie che ne contengono almeno uno.
 * I filtri sono gli stessi che prima finivano nella querystring PostgREST e ora sono
 * parametri della RPC.
 */
export async function fetchPartnerServices(
  token: string | null,
  filters?: PartnerServiceFilter
): Promise<{ services: Service[]; categories: ServiceCategory[] }> {
  const result = await portalRpc<PartnerServicesResult | null>(
    PORTAL,
    'portal_partner_services',
    token,
    {
      p_category_id: filters?.category_id || null,
      p_search: filters?.search || null,
      p_service_type: filters?.service_type || null,
      p_price_min: filters?.price_min ?? null,
      p_price_max: filters?.price_max ?? null
    }
  );

  const services = asArray<unknown>(result?.services) as unknown as Service[];
  const categories = asArray<unknown>(result?.categories) as unknown as ServiceCategory[];

  logPartnerActivity(
    token,
    'view_services',
    `Viewed services catalog${filters?.search ? ` (search: ${filters.search})` : ''}`,
    'services',
    null,
    { filters: filters ?? null, results_count: services.length }
  ).catch(() => {});

  return { services, categories };
}

/**
 * Dettaglio di un servizio. La RPC verifica che appartenga all'organizzazione del partner:
 * prima bastava un id qualsiasi con `partner_visible = true` per leggere il listino altrui.
 */
export async function fetchPartnerServiceDetail(
  token: string | null,
  serviceId: string
): Promise<Service> {
  const data = await portalRpc<unknown>(PORTAL, 'portal_partner_service_detail', token, {
    p_service_id: serviceId
  });

  if (!data) {
    throw new Error('Servizio non trovato o non accessibile');
  }

  return data as Service;
}

/** Richieste di preventivo di questo partner. I filtri si applicano sulla lista restituita. */
export async function fetchPartnerServiceRequests(
  token: string | null,
  filters?: PartnerRequestsFilter
): Promise<PartnerServiceRequest[]> {
  const raw = await portalRpc<unknown>(PORTAL, 'portal_partner_service_requests', token);
  let requests = asArray<unknown>(raw) as unknown as PartnerServiceRequest[];

  if (!filters) return requests;

  if (filters.search) {
    const needle = filters.search.toLowerCase();
    requests = requests.filter((r) =>
      [r.request_title, r.request_description]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle))
    );
  }

  if (filters.status) requests = requests.filter((r) => r.status === filters.status);
  if (filters.priority) requests = requests.filter((r) => r.priority === filters.priority);
  if (filters.service_id) requests = requests.filter((r) => r.service_id === filters.service_id);
  if (filters.date_from) requests = requests.filter((r) => r.created_at >= filters.date_from!);
  if (filters.date_to) requests = requests.filter((r) => r.created_at <= filters.date_to!);

  return requests;
}

/**
 * Nuova richiesta di preventivo. Le colonne di gestione interna (stato, importo preventivato,
 * note interne) non sono impostabili dal portale, e `is_multi_service` lo deriva il server dai
 * servizi citati: il client non può più violare il vincolo check_service_or_services.
 */
export async function createPartnerServiceRequest(
  token: string | null,
  requestData: PartnerServiceRequestFormData & {
    services?: { service_id: string; service_title: string }[];
  }
): Promise<PartnerServiceRequest | null> {
  const payload = {
    service_id: requestData.service_id ?? null,
    services: requestData.services ?? null,
    request_title: requestData.request_title,
    request_description: requestData.request_description ?? null,
    estimated_budget: requestData.estimated_budget ?? null,
    target_start_date: requestData.target_start_date ?? null,
    additional_requirements: requestData.additional_requirements ?? null,
    priority: requestData.priority || 'medium'
  };

  const result = await portalRpc<unknown>(PORTAL, 'portal_partner_create_service_request', token, {
    p_data: payload
  });

  return (result as PartnerServiceRequest) ?? null;
}

/** Registro attività del partner. */
export async function fetchPartnerActivity(
  token: string | null,
  limit = 50
): Promise<PartnerActivityLog[]> {
  const raw = await portalRpc<unknown>(PORTAL, 'portal_partner_activity', token, {
    p_limit: limit
  });
  return asArray<unknown>(raw) as unknown as PartnerActivityLog[];
}

/**
 * Registra un evento di consultazione non coperto dalle altre RPC.
 * Le RPC che scrivono dati loggano già per conto proprio: non vanno raddoppiate.
 */
export async function logPartnerActivity(
  token: string | null,
  activityType: string,
  description?: string | null,
  resourceType?: string | null,
  resourceId?: string | null,
  additional?: Record<string, unknown> | null
): Promise<void> {
  if (!ALLOWED_ACTIVITY_TYPES.has(activityType)) return;

  try {
    await portalRpc<boolean>(PORTAL, 'portal_partner_log_activity', token, {
      p_activity_type: activityType,
      p_description: description ?? null,
      p_resource_type: resourceType ?? null,
      p_resource_id: resourceId ?? null,
      p_additional: additional ?? null
    });
  } catch (err) {
    console.warn('Impossibile registrare l\'attività del partner:', err);
  }
}
